import {
    AntdLoginForm,
    AnyError, ILoginFormState,
    LoginFormContext
} from "@ib-tehnologije/react-widgets";
import React, {FC, useCallback, useContext, useMemo, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {AuthContext} from "../Contexts/AuthContext";
import {LoginContext} from "../Contexts/LoginContext";
import {ApolloError} from "@apollo/client";
import {App, Button, Form, Input, Modal, Result, Typography} from "antd";
import {buildUserFacingErrorLabels, getUserFacingErrorMessage} from "../userFacingErrorUtils";
import {
    useCompleteOrganizationMemberOnboardingMutation,
    useCompleteOrganizerPasswordResetMutation,
    useLogMeInMutation,
    useRequestOrganizerPasswordResetMutation,
    useResendConfirmationEmailMutation
} from "../GraphQL/generated/graphql";
import {buildPasswordRecoveryInput, PasswordRecoveryFormValues, resolveLoginReturnPath} from "./loginPageHelpers";

import logo from '../Images/ati.svg';

type OrganizationMemberOnboardingFormValues = {
    newPassword: string;
    confirmPassword: string;
};

type OrganizerPasswordResetFormValues = {
    newPassword: string;
    confirmPassword: string;
};

const LoginPage: FC = () => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const {setToken} = useContext(AuthContext);
    const {loginState, setLoginState} = useContext(LoginContext);
    const {state: loginFormState} = useContext(LoginFormContext);
    const [login] = useLogMeInMutation();
    const [resendConfirmationEmail] = useResendConfirmationEmailMutation();
    const [requestOrganizerPasswordReset, {loading: requestingPasswordReset}] = useRequestOrganizerPasswordResetMutation();
    const [completeOrganizerPasswordReset, {loading: completingPasswordReset}] = useCompleteOrganizerPasswordResetMutation();
    const [completeOrganizationMemberOnboarding, {loading: completingOnboarding}] = useCompleteOrganizationMemberOnboardingMutation();
    const [resendingConfirmationEmail, setResendingConfirmationEmail] = useState(false);
    const [passwordRecoveryOpen, setPasswordRecoveryOpen] = useState(false);
    const [passwordRecoveryRequested, setPasswordRecoveryRequested] = useState(false);
    const [passwordRecoveryForm] = Form.useForm<PasswordRecoveryFormValues>();
    const [passwordResetForm] = Form.useForm<OrganizerPasswordResetFormValues>();
    const [passwordResetComplete, setPasswordResetComplete] = useState(false);
    const [passwordResetError, setPasswordResetError] = useState<string>();
    const [onboardingForm] = Form.useForm<OrganizationMemberOnboardingFormValues>();
    const [onboardingError, setOnboardingError] = useState<string>();

    const [loginError, setLoginError] = useState<AnyError<any> | undefined>(undefined);

    const organizationMemberOnboarding = useMemo(() => {
        const query = new URLSearchParams(location.search);
        if (query.get("organizationMemberOnboarding") !== "1") {
            return null;
        }

        return {
            userId: query.get("userId") ?? "",
            emailConfirmationToken: query.get("emailConfirmationToken") ?? "",
            passwordResetToken: query.get("passwordResetToken") ?? ""
        };
    }, [location.search]);

    const organizationMemberOnboardingLinkIsComplete = useMemo(() => {
        if (!organizationMemberOnboarding) {
            return false;
        }

        return Boolean(
            organizationMemberOnboarding.userId &&
            organizationMemberOnboarding.emailConfirmationToken &&
            organizationMemberOnboarding.passwordResetToken);
    }, [organizationMemberOnboarding]);

    const organizerPasswordReset = useMemo(() => {
        const query = new URLSearchParams(location.search);
        if (query.get("passwordReset") !== "1") {
            return null;
        }

        return {
            userId: query.get("userId") ?? "",
            passwordResetToken: query.get("passwordResetToken") ?? ""
        };
    }, [location.search]);

    const organizerPasswordResetLinkIsComplete = useMemo(() => Boolean(
        organizerPasswordReset?.userId && organizerPasswordReset?.passwordResetToken), [organizerPasswordReset]);

    const userEmailNotConfirmed = useMemo(() => {
        if (loginError) {
            if (loginError instanceof ApolloError) {
                const apolloError = loginError as ApolloError;
                if (apolloError.networkError) {
                    const resultData = (apolloError.networkError as any).result;
                    if (resultData) {
                        const firstNetworkError = resultData?.errors[0];
                        if (firstNetworkError.extensions) {
                            const extension = firstNetworkError.extensions;
                            if (extension.message === "User email not confirmed" || extension?.INNER_EXCEPTION === "User email not confirmed") {
                                return true;
                            }
                        }
                    }
                }
            }
        }
        return false;
    }, [loginError]);

    const submitted = useCallback(async (data: ILoginFormState) => {
        setLoginState({});
        setLoginError(undefined);

        try {
            const loginResponse = await login({
                variables: {
                    username: data.username,
                    password: data.password
                }
            });

            if (loginResponse.data) {
                setToken(loginResponse.data.login.token);
                navigate(resolveLoginReturnPath(location.search));
            }
        } catch (e) {
            setLoginError(e as AnyError<any>);
            throw e;
        }
    }, [location.search, navigate, login]);

    const resendConfirmationEmailClicked = useCallback(async () => {
        setResendingConfirmationEmail(true);
        try {
            await resendConfirmationEmail({
                variables: {
                    model: {
                        username: loginFormState.username
                    }
                }
            });
            setLoginState({resendConfirmationEmailSuccess: true});
            setLoginError(undefined);
        } catch (e) {
            setLoginState({});
            setLoginError(undefined);
            message.error(getUserFacingErrorMessage(e, t, buildUserFacingErrorLabels(t)));
        } finally {
            setResendingConfirmationEmail(false);
        }
    }, [loginFormState, message, resendConfirmationEmail, t]);

    const openPasswordRecovery = useCallback(() => {
        setLoginState({});
        setLoginError(undefined);
        setPasswordRecoveryRequested(false);
        passwordRecoveryForm.setFieldsValue({
            username: loginFormState.username
        });
        setPasswordRecoveryOpen(true);
    }, [loginFormState.username, passwordRecoveryForm]);

    const passwordRecoverySubmitted = useCallback(async () => {
        const values = await passwordRecoveryForm.validateFields();

        try {
            await requestOrganizerPasswordReset({
                variables: {
                    ...buildPasswordRecoveryInput(values)
                }
            });
            passwordRecoveryForm.resetFields();
            setPasswordRecoveryOpen(false);
            setPasswordRecoveryRequested(true);
            setLoginState({});
            setLoginError(undefined);
        } catch (e) {
            message.error(getUserFacingErrorMessage(e, t, buildUserFacingErrorLabels(t)));
        }
    }, [message, passwordRecoveryForm, requestOrganizerPasswordReset, t]);

    const organizerPasswordResetSubmitted = useCallback(async () => {
        if (!organizerPasswordReset || !organizerPasswordResetLinkIsComplete) {
            setPasswordResetError(t("organizer-password-reset-invalid", {
                defaultValue: "This password reset link is invalid or expired. Request a new link."
            }));
            return;
        }

        const values = await passwordResetForm.validateFields();

        try {
            await completeOrganizerPasswordReset({
                variables: {
                    input: {
                        userId: organizerPasswordReset.userId,
                        passwordResetToken: organizerPasswordReset.passwordResetToken,
                        newPassword: values.newPassword
                    }
                }
            });
            passwordResetForm.resetFields();
            setPasswordResetComplete(true);
            setPasswordResetError(undefined);
            setLoginState({});
            setLoginError(undefined);
        } catch (e) {
            setPasswordResetError(t("organizer-password-reset-invalid", {
                defaultValue: "This password reset link is invalid or expired. Request a new link."
            }));
        }
    }, [
        completeOrganizerPasswordReset,
        organizerPasswordReset,
        organizerPasswordResetLinkIsComplete,
        passwordResetForm,
        setLoginState,
        t
    ]);

    const organizationMemberOnboardingSubmitted = useCallback(async () => {
        if (!organizationMemberOnboarding || !organizationMemberOnboardingLinkIsComplete) {
            setOnboardingError(t("organization-member-onboarding-invalid", {
                defaultValue: "This onboarding link is invalid or expired."
            }));
            return;
        }

        const values = await onboardingForm.validateFields();

        try {
            await completeOrganizationMemberOnboarding({
                variables: {
                    input: {
                        userId: organizationMemberOnboarding.userId,
                        emailConfirmationToken: organizationMemberOnboarding.emailConfirmationToken,
                        passwordResetToken: organizationMemberOnboarding.passwordResetToken,
                        newPassword: values.newPassword
                    }
                }
            });
            onboardingForm.resetFields();
            setOnboardingError(undefined);
            setLoginState({});
            setLoginError(undefined);
            navigate("/Login", {replace: true});
        } catch (e) {
            setOnboardingError(t("organization-member-onboarding-invalid", {
                defaultValue: "This onboarding link is invalid or expired."
            }));
        }
    }, [
        completeOrganizationMemberOnboarding,
        onboardingForm,
        navigate,
        organizationMemberOnboarding,
        organizationMemberOnboardingLinkIsComplete,
        setLoginState,
        t
    ]);

    const showingSomeState = useMemo(() => {
        return loginState.justConfirmedEmail || loginState.appEntryError || loginState.resendConfirmationEmailSuccess || passwordRecoveryRequested || userEmailNotConfirmed;
    }, [loginState, passwordRecoveryRequested, userEmailNotConfirmed]);

    const clearAllState = useCallback(() => {
        setLoginState({});
        setLoginError(undefined);
        setPasswordRecoveryRequested(false);
        setPasswordResetComplete(false);
        setPasswordResetError(undefined);
        setOnboardingError(undefined);
    }, []);

    const goToLogin = useCallback(() => {
        clearAllState();
        navigate("/Login", {replace: true});
    }, [clearAllState, navigate]);

    if (organizerPasswordReset) {
        if (!organizerPasswordResetLinkIsComplete || passwordResetError) {
            return <Result
                status="error"
                title={t("organizer-password-reset-invalid-title", {defaultValue: "Password reset link cannot be used"})}
                subTitle={passwordResetError ?? t("organizer-password-reset-invalid", {
                    defaultValue: "This password reset link is invalid or expired. Request a new link."
                })}
                extra={[
                    <Button type="primary" key="goToLogin" onClick={goToLogin}>
                        {t("BackGoToLogin")}
                    </Button>,
                ]}
            />;
        }

        if (passwordResetComplete) {
            return <Result
                status="success"
                title={t("password-reset-successful", {defaultValue: "Password Reset Successful"})}
                subTitle={t("organizer-password-reset-complete-help", {defaultValue: "Use your new organizer password to sign in."})}
                extra={[
                    <Button type="primary" key="goToLogin" onClick={goToLogin}>
                        {t("BackGoToLogin")}
                    </Button>,
                ]}
            />;
        }

        return <div style={{maxWidth: 550, margin: "80px auto", padding: "0 24px"}}>
            <div className="ati-stack ati-stack--loose">
                <div style={{textAlign: "center"}}>
                    <img alt={t("ati-logo-alt", {defaultValue: "ATI logo"})} src={logo} style={{maxWidth: 160, width: "60%"}}/>
                </div>
                <div className="ati-stack ati-stack--tight">
                    <Typography.Title level={2} style={{marginBottom: 0}}>
                        {t("organizer-password-reset-title", {defaultValue: "Choose a new password"})}
                    </Typography.Title>
                    <Typography.Paragraph type="secondary">
                        {t("organizer-password-reset-help", {defaultValue: "Enter a new password for your organizer account."})}
                    </Typography.Paragraph>
                </div>
                <Form form={passwordResetForm} layout="vertical" preserve={false}>
                    <Form.Item
                        name="newPassword"
                        label={t("new-password", {defaultValue: "New password"})}
                        rules={[
                            {required: true, message: t("new-password-required", {defaultValue: "New password is required."})},
                            {min: 8, message: t("new-password-min-length", {count: 8, defaultValue: "Use at least {{count}} characters."})}
                        ]}
                    >
                        <Input.Password autoComplete="new-password"/>
                    </Form.Item>
                    <Form.Item
                        name="confirmPassword"
                        label={t("confirm-new-password", {defaultValue: "Confirm new password"})}
                        dependencies={["newPassword"]}
                        rules={[
                            {required: true, message: t("confirm-new-password-required", {defaultValue: "Confirm the new password."})},
                            ({getFieldValue}) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue("newPassword") === value) {
                                        return Promise.resolve();
                                    }

                                    return Promise.reject(new Error(t("passwords-do-not-match", {defaultValue: "Passwords do not match."})));
                                }
                            })
                        ]}
                    >
                        <Input.Password autoComplete="new-password"/>
                    </Form.Item>
                    <Button
                        type="primary"
                        loading={completingPasswordReset}
                        onClick={organizerPasswordResetSubmitted}
                    >
                        {t("organizer-password-reset-submit", {defaultValue: "Set new password"})}
                    </Button>
                </Form>
            </div>
        </div>;
    }

    if (organizationMemberOnboarding) {
        if (!organizationMemberOnboardingLinkIsComplete || onboardingError) {
            return <Result
                status="error"
                title={t("organization-member-onboarding-invalid-title", {defaultValue: "Onboarding link cannot be used"})}
                subTitle={onboardingError ?? t("organization-member-onboarding-invalid", {
                    defaultValue: "This onboarding link is invalid or expired."
                })}
                extra={[
                    <Button type="primary" key="goToLogin" onClick={goToLogin}>
                        {t("BackGoToLogin")}
                    </Button>,
                ]}
            />;
        }

        return <div style={{maxWidth: 550, margin: "80px auto", padding: "0 24px"}}>
            <div className="ati-stack ati-stack--loose">
                <div style={{textAlign: "center"}}>
                    <img alt={t("ati-logo-alt", {defaultValue: "ATI logo"})} src={logo} style={{maxWidth: 160, width: "60%"}}/>
                </div>
                <div className="ati-stack ati-stack--tight">
                    <Typography.Title level={2} style={{marginBottom: 0}}>
                        {t("organization-member-onboarding-title", {defaultValue: "Set up organizer account"})}
                    </Typography.Title>
                    <Typography.Paragraph type="secondary">
                        {t("organization-member-onboarding-help", {defaultValue: "Choose your password to confirm the invitation."})}
                    </Typography.Paragraph>
                </div>
                <Form form={onboardingForm} layout="vertical" preserve={false}>
                    <Form.Item
                        name="newPassword"
                        label={t("new-password", {defaultValue: "New password"})}
                        rules={[
                            {required: true, message: t("new-password-required", {defaultValue: "New password is required."})},
                            {min: 6, message: t("new-password-min-length", {count: 6, defaultValue: "Use at least {{count}} characters."})}
                        ]}
                    >
                        <Input.Password autoComplete="new-password"/>
                    </Form.Item>
                    <Form.Item
                        name="confirmPassword"
                        label={t("confirm-new-password", {defaultValue: "Confirm new password"})}
                        dependencies={["newPassword"]}
                        rules={[
                            {required: true, message: t("confirm-new-password-required", {defaultValue: "Confirm the new password."})},
                            ({getFieldValue}) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue("newPassword") === value) {
                                        return Promise.resolve();
                                    }

                                    return Promise.reject(new Error(t("passwords-do-not-match", {defaultValue: "Passwords do not match."})));
                                }
                            })
                        ]}
                    >
                        <Input.Password autoComplete="new-password"/>
                    </Form.Item>
                    <Button
                        type="primary"
                        loading={completingOnboarding}
                        onClick={organizationMemberOnboardingSubmitted}
                    >
                        {t("organization-member-onboarding-submit", {defaultValue: "Set password"})}
                    </Button>
                </Form>
            </div>
        </div>;
    }

    return <div>
        {showingSomeState ? null :
            <>
                <AntdLoginForm submitted={submitted} logoSrc={logo} logoFullWidth/>
                <div style={{maxWidth: 550, margin: "-80px auto 60px", padding: "0 24px", textAlign: "right"}}>
                    <Button type="link" onClick={openPasswordRecovery}>
                        {t("forgot-password", {defaultValue: "Forgot password?"})}
                    </Button>
                </div>
            </>}
        <Modal
            title={t("organizer-password-recovery", {defaultValue: "Organizer password recovery"})}
            open={passwordRecoveryOpen}
            onCancel={() => setPasswordRecoveryOpen(false)}
            onOk={passwordRecoverySubmitted}
            confirmLoading={requestingPasswordReset}
            okText={t("organizer-password-recovery-submit", {defaultValue: "Send instructions"})}
            cancelText={t("cancel", {defaultValue: "Cancel"})}
            destroyOnHidden
        >
            <div className="ati-stack">
                <Typography.Paragraph type="secondary">
                    {t("organizer-password-recovery-help", {defaultValue: "Use your organizer email address to reset your password. Administrators cannot assign or view organizer passwords."})}
                </Typography.Paragraph>
                <Form form={passwordRecoveryForm} layout="vertical" preserve={false}>
                    <Form.Item
                        name="username"
                        label={t("organizer-email", {defaultValue: "Organizer email"})}
                        rules={[
                            {required: true, message: t("organizer-email-required", {defaultValue: "Organizer email is required."})},
                            {type: "email", message: t("InvalidEmail", {defaultValue: "Enter a valid email"})}
                        ]}
                    >
                        <Input autoComplete="username"/>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
        {loginState.justConfirmedEmail &&
            <Result
                style={{
                    verticalAlign: "middle",
                    margin: "auto",
                }}
                status="success"
                title={t("EmailConfirmed_Success")}
                extra={[
                    <Button type="primary" key="gotologin" onClick={() => setLoginState({})}>
                        {t("BackGoToLogin")}
                    </Button>,
                ]}
            />}
        {loginState.appEntryError && <Result
            status="error"
            title={t(loginState.appEntryError)}
            extra={[
                <Button type="primary" key="gotologin" onClick={clearAllState}>
                    {t("BackGoToLogin")}
                </Button>,
            ]}
        />}
        {userEmailNotConfirmed && <Result
            status="error"
            title={t("EmailNotConfirmed")}
            extra={[
                <Button type="primary" key="goToLogin" onClick={() => setLoginError(undefined)}>
                    {t("BackGoToLogin")}
                </Button>,
                <Button key="resendConfirmationEmail" loading={resendingConfirmationEmail} onClick={resendConfirmationEmailClicked}>
                    {t("ResendConfirmationEmail")}
                </Button>,
            ]}/>}
        {loginState.resendConfirmationEmailSuccess && <Result
            status="success"
            title={t("ResendConfirmationEmail_Success")}
            extra={[
                <Button type="primary" key="goToLogin" onClick={() => setLoginState({})}>
                    {t("BackGoToLogin")}
                </Button>,
            ]}/>}
        {passwordRecoveryRequested && <Result
            status="success"
            title={t("password-recovery-requested-title", {defaultValue: "Check your email"})}
            subTitle={t("password-recovery-success-help", {defaultValue: "If that organizer exists, password recovery instructions have been sent."})}
            extra={[
                <Button type="primary" key="goToLogin" onClick={clearAllState}>
                    {t("BackGoToLogin")}
                </Button>,
            ]}/>}
    </div>;
}

export default LoginPage;
